import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import getAllLeads from "./apis/getAllLeads";
import "react-datepicker/dist/react-datepicker.css";

const Leads = ({ botId }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [originalLeads, setOriginalLeads] = useState([]);
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [sort, setSort] = useState("");

  useEffect(() => {
    const fetchLeads = async () => {
      const res = await getAllLeads(botId, startDate, endDate, page);
      if (res.error) return;
      setOriginalLeads(res.data);
      setPagination(res.pagination);
    };

    if (botId) fetchLeads();
  }, [botId, startDate, endDate, page]);

  useEffect(() => {
    let sorted = [...originalLeads];
    switch (sort) {
      case "name-asc":
        sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        break;
      case "name-desc":
        sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        break;
      case "created-asc":
        sorted.sort((a, b) => a.id - b.id);
        break;
      case "created-desc":
        sorted.sort((a, b) => b.id - a.id);
        break;
      default:
        break;
    }
    setLeads(sorted);
  }, [sort, originalLeads]);

  const exportCSV = () => {
    const headers = ["ID", "Name", "Email", "Phone"];
    const rows = leads.map((l) => [l.id, l.name, l.email, l.phone]);
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell || ""}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "leads.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex flex-col border rounded-md shadow-md p-4 space-y-4 w-full max-w-full">
      {/* Filters */}
      <div className="flex flex-col md:flex-row md:justify-between gap-3 items-stretch md:items-center w-full">
        <div className="flex flex-wrap gap-2">
          <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
            className="border px-3 py-2 rounded-md shadow-sm w-40"
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText="End Date"
            className="border px-3 py-2 rounded-md shadow-sm w-40"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border px-3 py-2 rounded-md shadow-sm w-40">
            <option value="">Sort By</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
            <option value="created-asc">Created: Oldest First</option>
            <option value="created-desc">Created: Newest First</option>
          </select>
        </div>

        <button
          onClick={exportCSV}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm whitespace-nowrap">
          Export CSV
        </button>
      </div>

      {/* Leads Table */}
      <div className="overflow-auto rounded-xl shadow max-w-full">
        <table className="min-w-full text-left font-medium text-sm text-gray-800">
          <thead className="bg-gray-800 text-white uppercase">
            <tr>
              {["Id", "Name", "Email", "Phone"].map((heading, index) => (
                <th key={index} className="px-6 py-3 whitespace-nowrap">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center px-6 py-4 text-gray-500">
                  No leads found
                </td>
              </tr>
            ) : (
              leads.map((lead, i) => (
                <tr
                  key={lead.id || i}
                  className="bg-white hover:bg-gray-100 transition duration-300">
                  <td className="px-6 py-3">{lead.id}</td>
                  <td className="px-6 py-3">{lead.name || "N/A"}</td>
                  <td className="px-6 py-3">{lead.email || "N/A"}</td>
                  <td className="px-6 py-3">{lead.phone || "N/A"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <div className="flex justify-between items-center mt-2 text-sm flex-wrap gap-2">
          <span className="text-gray-600">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <div className="space-x-2">
            <button
              className="px-3 py-1 border rounded-md disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
            <button
              className="px-3 py-1 border rounded-md disabled:opacity-50"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
